import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from 'atoms/layout/flex';
import { Snackbar } from 'atoms/snackbar/Snackbar';
import { LabelL } from 'atoms/typography';
import { SpentProgress } from 'components/wallet/BankEthAccounting/AddFundsToFiatWallet/promo/SpentProgress';
import type { PaymentMethod as DepositMethod } from 'components/wallet/BankEthAccounting/AddFundsToFiatWallet/types';
import { CreditCardPaymentMethod } from 'components/wallet/BankEthAccounting/AddFundsToFiatWallet/types';
import { useCurrentUserContext } from 'contexts/currentUser';
import type { Lifecycle } from 'hooks/useLifecycle';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import type { MonetaryAmount } from 'lib/monetaryAmount';

import { DepositContext } from '.';

interface Props {
  children: ReactNode;
}

const DepositActivePromotionalSpentNotification = () => {
  const { currentUser } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const [previousActivePromotionalSpent, setPreviousActivePromotionalSpent] =
    useState<MonetaryAmount | undefined>();
  const [open, setOpen] = useState(false);

  if (
    !previousActivePromotionalSpent &&
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion
  ) {
    setPreviousActivePromotionalSpent(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    );
  }

  if (
    previousActivePromotionalSpent &&
    currentUser?.completablePromotionalEvent?.promotionalEventCompletion &&
    !open &&
    !previousActivePromotionalSpent.isEqualTo(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    )
  ) {
    setPreviousActivePromotionalSpent(
      toMonetaryAmount(
        currentUser.completablePromotionalEvent.promotionalEventCompletion
          .actualAmounts
      )
    );
    setOpen(true);
  }

  if (!open) return null;

  return (
    <Snackbar
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      autoHideDuration={4000}
      message={
        <Vertical style={{ minWidth: '300px' }}>
          <LabelL bold>
            <FormattedMessage
              id="DepositActivePromotionalSpentNotification.winYourBox"
              defaultMessage="Win your reward box"
            />
          </LabelL>
          <SpentProgress />
        </Vertical>
      }
    />
  );
};

export const DepositProvider = ({ children }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const preferredDepositMethod = (
    currentUser?.userSettings.lifecycle as Lifecycle
  )?.preferredDepositMethod;
  const [defaultDepositMethod, setDefaultDepositMethod] = useState<
    DepositMethod | undefined
  >(
    preferredDepositMethod !== CreditCardPaymentMethod.CREDIT_CARD
      ? preferredDepositMethod
      : undefined
  );

  return (
    <DepositContext.Provider
      value={{ defaultDepositMethod, setDefaultDepositMethod }}
    >
      {children}
      <DepositActivePromotionalSpentNotification />
    </DepositContext.Provider>
  );
};
